// Health functions
import * as locCon from '../LocalConstants';
import * as libCon from '../community-hats-js-library/Constants';
import { getATOField, getATOId, getStoredValue } from '../hooks/StoreHooks';
import {
  DATE_STRING_EXPORT,
  formatInStudyLocationToCustom,
  formatToStudyLocation,
} from '../community-hats-js-library/utils/dateFunctions';
import { filterObjectByFunction, isNullOrUndefined } from '../community-hats-js-library/utils/generalFunctions';

export const buildGenericParticipantSurveyMeta = () => {
  return {
    [libCon.SURVEY_EXPORT_PARTICIPANT_ID]: getATOId(locCon.AT_OBJECT_PARTICIPANT),
    [libCon.SURVEY_EXPORT_SEWA_ID]: getATOField(locCon.AT_OBJECT_PARTICIPANT, libCon.ATF_SEWA_ID),
    [libCon.SURVEY_EXPORT_PHONE_SERIAL]: getATOField(locCon.AT_OBJECT_PHONE, libCon.ATF_SERIAL),
    [libCon.SURVEY_EXPORT_DATETIME]: formatToStudyLocation(new Date(), true),
    [libCon.SURVEY_EXPORT_LOCALE]: getStoredValue(locCon.STORAGE_CURRENT_LOCAL),
  };
};

export const buildPerceptualSurveyMeta = () => {
  return buildGenericParticipantSurveyMeta();
};

export const buildPerceptualWomensHealthSurveyMeta = () => {
  return buildGenericParticipantSurveyMeta();
};

export const buildParticipantIntakeFormMeta = () => {
  return buildGenericParticipantSurveyMeta();
};

export const buildHouseIntakeFormMeta = () => {
  return {
    [libCon.SURVEY_EXPORT_PHONE_SERIAL]: getATOField(locCon.AT_OBJECT_PHONE, libCon.ATF_SERIAL),
    [libCon.SURVEY_EXPORT_DATETIME]: formatToStudyLocation(new Date(), true),
    [libCon.SURVEY_EXPORT_LOCALE]: getStoredValue(locCon.STORAGE_CURRENT_LOCAL),
    [libCon.SURVEY_EXPORT_HOUSE_NUMBER]: getATOField(locCon.AT_OBJECT_HOUSE, libCon.ATF_NUMERIC_ID),
    [libCon.SURVEY_EXPORT_HOUSE_ID]: getATOId(locCon.AT_OBJECT_HOUSE),
  };
};

export const buildSurveyExport = (surveyStructure, encoder, buildMeta) => {
  if (surveyStructure === null || surveyStructure === undefined) return null;

  // Answers
  const answers = surveyStructure[libCon.SURVEY_STRUCTURE].reduce((res, q) => {
    return Object.assign(res, {
      [q[libCon.SURVEY_QUESTION_ID]]: getStoredValue(encoder(q[libCon.SURVEY_QUESTION_ID])),
    });
  }, {});

  let sewaId = getATOField(locCon.AT_OBJECT_PARTICIPANT, libCon.ATF_SEWA_ID);
  if (isNullOrUndefined(sewaId)) sewaId = libCon.MISSING;

  // Meta
  let surveyMeta = buildMeta();
  let meta = {
    ...surveyStructure[libCon.SURVEY_METADATA],
    ...surveyMeta,
  };

  const response = {
    [libCon.SURVEY_EXPORT_CONFIGURATION]: surveyStructure,
    [libCon.SURVEY_EXPORT_ANSWERS]: answers,
    [libCon.SURVEY_METADATA]: meta,
  };

  return response;
};

export const buildSurveyFilename = () => {
  let sewa_id = getATOField(locCon.AT_OBJECT_PARTICIPANT, libCon.ATF_SEWA_ID);
  if (sewa_id === null) sewa_id = libCon.MISSING;

  return `${sewa_id}__${formatInStudyLocationToCustom(new Date(), DATE_STRING_EXPORT)}.json`;
};

export const buildHouseSurveyFilename = () => {
  let house = getATOField(locCon.AT_OBJECT_HOUSE, libCon.ATF_NUMERIC_ID);
  if (house === null) house = libCon.MISSING;

  return `${house}__${formatInStudyLocationToCustom(new Date(), DATE_STRING_EXPORT)}.json`;
};

export const encodeSurveyId = (prefix, id) => {
  return `${prefix}${id}`;
};

export const decodeSurveyId = (prefix, id) => {
  if (id === null || id === undefined) return id;
  return `${id}`.replace(prefix, '');
};

export const encodeSurveyFormId = (survey_prefix) => (id) => {
  return encodeSurveyId(survey_prefix, id);
};

export const hasPendingQuestions = (missingQuestions) => {
  if (isNullOrUndefined(missingQuestions)) return false;

  return (
    Object.values(filterObjectByFunction(missingQuestions, (ob) => ob[libCon.ATF_STATUS] === libCon.ATF_PENDING))
      .length > 0
  );
};

export const getPendingQuestionsIdsForSurvey = (missingQuestions, surveyName) => {
  if (isNullOrUndefined(missingQuestions)) return [];

  return Object.values(
    filterObjectByFunction(
      missingQuestions,
      (ob) => ob[libCon.ATF_STATUS] === libCon.ATF_PENDING && ob[libCon.ATF_FORM_TYPE] === surveyName
    )
  ).map((ob) => ob[libCon.ATF_QUESTION_ID]);
};

export const constructFollowUpConfiguration = (configuration) => {
  let newConf = {};
  Object.keys(configuration).forEach((k) => {
    if (k.includes(libCon.SURVEY_QUESTION_FOLLOWUP_PREFIX))
      newConf[k.replace(libCon.SURVEY_QUESTION_FOLLOWUP_PREFIX, '')] = configuration[k];
  });

  return newConf;
};
